const getDictionaryRoot = () => (
  globalThis?.window?.dic
  || globalThis?.dic
  || null
);

const resolveDicRef = (value) => {
  if (typeof value !== 'string') return value;

  const raw = value.trim();
  const unquoted = raw.replace(/^['"`\s]+|['"`\s]+$/g, '');
  if (!/^dic\./i.test(unquoted)) return value;

  const parts = unquoted.replace(/^dic\./i, '').split('.').filter(Boolean);
  let cursor = getDictionaryRoot();

  for (const part of parts) {
    if (!cursor || typeof cursor !== 'object') return value;
    if (Object.prototype.hasOwnProperty.call(cursor, part)) {
      cursor = cursor[part];
      continue;
    }

    const fallbackKey = Object.keys(cursor).find(
      (key) => String(key).toLowerCase() === part.toLowerCase()
    );
    if (!fallbackKey) return value;
    cursor = cursor[fallbackKey];
  }

  if (typeof cursor === 'string' || typeof cursor === 'number') {
    return String(cursor);
  }

  return value;
};

const codeVariants = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return [];

  return Array.from(new Set([
    normalized,
    normalized.toLowerCase(),
    normalized.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
    normalized
      .toLowerCase()
      .replace(/[^a-z0-9]+([a-z0-9])/g, (_, char) => char.toUpperCase()),
  ]));
};

const resolveCodeMessage = (value, dictionary) => {
  for (const variant of codeVariants(value)) {
    const candidates = [
      ...(dictionary ? [`dic.${dictionary}.errors.${variant}`] : []),
      `dic.errors.${variant}`,
      `dic.erros.${variant}`,
    ];

    for (const candidate of candidates) {
      const resolved = resolveDicRef(candidate);
      if (typeof resolved === 'string' && resolved !== candidate) {
        return resolved;
      }
    }
  }

  return null;
};

const DATABASE_CODE_MESSAGES = Object.freeze({
  '23502': 'dic.message.fillInAllFields',
  '23503': 'dic.message.invalidReference',
  '23505': 'dic.message.duplicateRecord',
  '23514': 'dic.message.constraintViolation',
});

/**
 * Resolve erros de backend usando o mesmo contrato de dicionário em qualquer
 * componente EP. Constraints nomeadas têm precedência sobre SQLSTATE genérico.
 */
export const resolveBackendErrorMessage = (result = {}, options = {}) => {
  const payload = result?.payload && typeof result.payload === 'object'
    ? result.payload
    : null;
  const dictionary =
    String(options?.dictionary || '').trim()
    || String(options?.module || '').trim()
    || String(options?.key || '').trim();

  const detailList = Array.isArray(result?.details)
    ? result.details
    : Array.isArray(payload?.details)
      ? payload.details
      : [];
  const firstDetailMessage = detailList
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object') {
        return String(item.message || item.error || item.code || '').trim();
      }
      return '';
    })
    .find(Boolean);

  const explicitRaw = result?.error
    || result?.message
    || payload?.error
    || payload?.message
    || firstDetailMessage;
  const explicitRawStr = typeof explicitRaw === 'string' ? explicitRaw.trim() : '';

  if (/^dic\./i.test(explicitRawStr)) {
    const resolved = resolveDicRef(explicitRawStr);
    if (typeof resolved === 'string' && resolved !== explicitRawStr) {
      return resolved;
    }

    if (dictionary) {
      for (const candidate of [
        explicitRawStr.replace(/^dic\.erros\./i, `dic.${dictionary}.errors.`),
        explicitRawStr.replace(/^dic\.errors?\./i, `dic.${dictionary}.errors.`),
      ]) {
        const scoped = resolveDicRef(candidate);
        if (typeof scoped === 'string' && scoped !== candidate) {
          return scoped;
        }
      }
    }
  }

  const constraint =
    result?.constraint
    || payload?.constraint
    || null;
  const constraintMessage = resolveCodeMessage(constraint, dictionary);
  if (constraintMessage) return constraintMessage;

  const code =
    result?.code
    || payload?.code
    || payload?.error
    || null;
  const codeMessage = resolveCodeMessage(code, dictionary);
  if (codeMessage) return codeMessage;

  const sqlState = String(result?.dbCode || payload?.dbCode || code || '').trim();
  const genericRef = DATABASE_CODE_MESSAGES[sqlState];
  if (genericRef) {
    const genericMessage = resolveDicRef(genericRef);
    if (typeof genericMessage === 'string' && genericMessage !== genericRef) {
      return genericMessage;
    }
  }

  const status = Number(result?.status || payload?.status || 0);
  if (status === 401 || String(code || '').toLowerCase() === 'not_authenticated') {
    const sessionExpired = resolveDicRef('dic.message.sessionExpired');
    if (sessionExpired !== 'dic.message.sessionExpired') return sessionExpired;
  }

  const transportOnlyMessage = /^HTTP\s+\d{3}(?:\s|$)/i.test(explicitRawStr);
  if (explicitRawStr && !/^dic\./i.test(explicitRawStr) && !(code && transportOnlyMessage)) {
    return explicitRawStr;
  }

  return null;
};


const WARNING_CODES = new Set([
  '23502',
  '23505',
  '23514',
  'DUPLICATE_COMPOSITE_KEY',
]);

export const resolveBackendErrorType = (result = {}) => {
  const payload = result?.payload && typeof result.payload === 'object'
    ? result.payload
    : null;
  const explicitType = String(result?.type || payload?.type || '').trim().toLowerCase();
  if (explicitType === 'warning' || explicitType === 'error') return explicitType;

  const code = String(
    result?.dbCode
    || result?.code
    || payload?.dbCode
    || payload?.code
    || payload?.error
    || ''
  ).trim().toUpperCase();

  return WARNING_CODES.has(code) ? 'warning' : 'error';
};

export default resolveBackendErrorMessage;
